<template>
  <div class="product">
    <transition name="fadein">
      <p v-if="loading">データを読み込んでいます。</p>
      <p v-else-if="error">{{ error }}</p>

      <div v-else-if="work">
        <div class="product-container">
          <div>
            <div class="info">
              <h3 class="info-title">{{ work.title }}</h3>
              <ul class="info-metadata">
                <li v-if="work.client">CL：{{ work.client }}</li>
                <li v-if="work.yearOfProduction">制作年：{{ work.yearOfProduction }}年</li>
                <li v-if="work.business && work.business.length > 0">{{ work.business.map(category => category.name).join(' / ') }}</li>
              </ul>
              <p class="info-description">{{ work.description }}</p>
              <ul v-if="work.credits && work.credits.length" class="credits">
                <li v-for="(credit, idx) in work.credits" :key="idx">{{ credit.kindof.join('、') }}：{{ credit.name.map(v => v.name).join('、') }}</li>
              </ul>
            </div>
          </div>
          
          <div>
            <div v-if="work.contents && work.contents.length" class="contents">
              <figure v-if="work.thumbnailUrl" class="contents-thumbnail">
                <img :src="work.thumbnailUrl" :alt="work.title" loading="eager">
              </figure>
              <div v-for="(item, index) in work.contents" :key="index">
                <figure v-if="item.acf_fc_layout === 'image'">
                  <img :src="getImageSrc(item.source)" :alt="(item.source && item.source.alt) || work.title" loading="lazy" :class="{ 'is-portrait': item.is_portrait }">
                  <figcaption v-if="item.caption">{{ item.caption }}</figcaption>
                </figure>
                <div v-if="item.acf_fc_layout === 'video'" class="contents-video-container">
                  <video v-if="item.source" :src="getVideoSrc(item.source)" controls></video>
                  <div v-if="item.embed" v-html="item.embed"></div>
                  <p v-if="item.caption">{{ item.caption }}</p>
                </div>
                <p v-else-if="item.acf_fc_layout === 'text' && item.chars">{{ item.chars }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="product-footer">
          <ul v-if="work.credits && work.credits.length" class="credits">
            <li v-for="(credit, idx) in work.credits" :key="idx">{{ credit.kindof.join('、') }}：{{ credit.name.map(v => v.name).join('、') }}</li>
          </ul>

          <div v-if="relatedWorks.length" class="related">
            <h3>「{{ work.client }}」関連プロダクト</h3>
  
            <div class="related-list">
              <router-link v-for="item in relatedWorks" :key="item.id" :to="`/client-work-demo/${item.id}`">
                <div class="related-thumbnail">
                  <img v-if="item.thumbnailUrl" :src="item.thumbnailUrl" :alt="item.title" loading="lazy">
                  <img v-else src="/assets/img/dummy.svg" loading="lazy" alt="no image">
                </div>
                <h2 class="related-product">{{ item.title }}</h2>
              </router-link>
            </div>
          </div>
  
          <div class="navigation">
            <router-link :to="work.prevPost ? `/client-work-demo/${work.prevPost.id}` : ''" :class="!work.prevPost && 'is-disabled'">前の事例</router-link>
            <router-link class="navigation-backto" to="/client-work-demo">制作事例一覧に戻る</router-link>
            <router-link :to="work.nextPost ? `/client-work-demo/${work.nextPost.id}` : ''" :class="!work.nextPost && 'is-disabled'">次の事例</router-link>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue'
import store from '@/store'
import { WorksItem, formatWorksItem, getWorksImageSrc, getWorksVideoSrc, formatRelatedWorks } from '@/types/works'

const WP_SITE_URL = process.env.NODE_ENV === 'development'
  ? process.env.VUE_APP_DEV_WP_SITE_URL
  : process.env.VUE_APP_PRO_WP_SITE_URL

export default Vue.extend({
  data() {
    return {
      work: null,
      relatedWorks: [],
      loading: true,
      error: ''
    }
  },

  async created() {
    await this.fetchWork()
  },

  watch: {
    '$route': 'fetchWork'
  },

  methods: {
    getImageSrc(image) {
      return getWorksImageSrc(image)
    },

    getVideoSrc(video) {
      return getWorksVideoSrc(video)
    },

    async fetchWork() {
      this.loading = true
      this.error = ''
      const id = this.$route.params.id
      const isPreview = this.$route.query.preview === 'true'
      const nonce = typeof this.$route.query.nonce === 'string' ? this.$route.query.nonce : undefined

      try {
        const query = new URLSearchParams({
          _embed: '1',
          acf_format: 'standard',
          status: isPreview ? 'draft' : 'publish'
        })
        const response = await fetch(`${WP_SITE_URL}/wp-json/wp/v2/works/${id}?${query}`, {
          headers: isPreview && nonce ? { 'X-WP-Nonce': nonce } : {}
        })
        const raw = await response.json()
        this.work = formatWorksItem(raw)
        this.relatedWorks = formatRelatedWorks(raw)

        if (this.work && this.work.title) {
          document.title = `${this.work.title}｜時の広告社｜茨城県水戸市のデザイン制作会社`
        }
      } catch (e) {
        console.error(e)
        if (e && e.status === 404) {
          this.error = '作品が見つかりませんでした。'
        } else {
          this.error = 'データの取得に失敗しました。'
        }
        this.work = null
      } finally {
        this.loading = false
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.product {
  width: 100%;
  @media (max-width: 1280px) {
    max-width: 50rem; 
    margin-inline: auto;
  }

  &-container {
    display: grid;
    grid-template-columns: 18rem 1fr;
    margin-bottom: 5rem;
    @media (max-width: 1280px) {
      grid-template-columns: auto;
      gap: 5rem;
    }
  }

  &-container > div:last-child {
    margin-left: -18rem;
    @media (max-width: 1280px) {
      margin-left: 0;
    }
  }

  &-footer {
    display: grid;
    width: 90%;
    @media (max-width: 1280px) {
      width: 100%;
    }
  }

  &-footer > .credits {
    display: none;
    @media (max-width: 1280px) {
      display: grid;
    }
  }

  &-footer > * {
    padding-block: 3rem;
    border-top: 1px solid #ddd;
    @media (max-width: 1280px) {
      width: 100%;
    }
    @media (max-width: 1024px) {
      width: calc(100% + 4rem);
      margin-inline: -2rem;
      padding-inline: 2rem;
    }
  }

  &-footer > * + * {
    border-top: 1px solid #ddd;
  }
}

.info {
  position: sticky;
  top: 10rem;
  
  &-title {
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: normal;
  }
  
  &-metadata {
    display: grid;
    gap: 0.25rem;
    margin-bottom: 2rem;
    color: #666;
    font-size: 0.75rem;
    list-style: none;

    > li:last-child {
      margin-top: 0.25rem;
    }
  }
  
  &-description {
    margin-bottom: 4rem;
    text-align: justify;
    line-height: 1.7;
    @media (max-width: 1280px) {
      margin-bottom: 0;
    }
  }
  
  .credits {
    @media (max-width: 1280px) {
      display: none;
    }
  }
}

.credits {
  display: grid;
  gap: 0.25rem;
  color: #666;
  font-size: 0.75rem;
  list-style: none;
}

.contents {
  container-type: inline-size;
  display: grid;
  gap: 4rem;
  width: 50%;
  margin: 0 auto;
  @media (max-width: 1280px) {
    gap: 3rem;
    width: 100%;
  }

  &-thumbnail {
    margin-bottom: 5rem;
  }

  figure,
  &-video-container {
    width: fit-content;
    margin-inline: auto;
  }

  figure:has(> img.is-portrait) {
    width: 100%;
  }

  figcaption,
  &-video-container > p {
    color: #666;
    font-size: 0.75rem;
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-height: 67cqw;
  }
  
  img.is-portrait {
    max-height: none;
  }

  iframe {
    width: 100cqw;
    height: auto;
    aspect-ratio: 1 / 0.5625;
  }
}

.related {
  padding-block: 5rem;
  
  > h3 {
    margin-bottom: 2rem;
    margin-left: -0.5em;
    font-weight: normal; 
  }
  
  &-list {
    display: grid;
    gap: 4rem;
    grid-template-columns: repeat(4, 1fr);
    @media (max-width: 1280px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;
    }
  }
  
  &-list > a:hover {
    text-decoration: none;
  }
  
  &-thumbnail {
    overflow: hidden;
    aspect-ratio: 1 / 0.8;
    margin-bottom: 1rem;
    font-size: 0;
  }
  
  &-thumbnail > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: scale 0.2s;
  }
  
  &-list > a:hover img {
    scale: 1.05;
  }
  
  &-product {
    margin-bottom: 0.25rem;
    padding-top: 0;
    font-size: 1rem;
    font-weight: normal;
    line-height: 1.3;
  }
  
  &-client {
    font-size: 0.75rem;
    color: gray;
  }
}

.navigation {
  display: flex;
  gap: 1rem;

  > * {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 5rem;
    height: 4rem;
    margin: auto;
    padding-inline: 2rem;
    letter-spacing: .25rem;
    border: 1px solid $color-tertiary;
    transition: .3s;
  }

  > *:hover {
    color: #fff;
    text-decoration: none;
    background: $color-primary;
  }

  > .is-disabled {
    opacity: .3;
    pointer-events: none;
  }

  &-backto {
    flex: 1;
  }
}
</style>